import { Alert, AlertDescription, AlertTitle, Button } from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';

import {
  FieldGrid,
  FieldGridItem,
  NotPermitted,
  PageHeader,
  ReasonDialog,
  StatusChip
} from '@/design';
import { useAuth, useCan } from '@/domains/auth';
import { formatDateTime } from '@/domains/survey';
import { internalFieldsApi } from '../../api/internal-fields-api';
import type {
  IListFieldCatalogue,
  IListRow,
  ReinstatementDecision
} from '../../api/lists-contracts';
import { listsApi } from '../../api/lists-api';
import type { IMyListTask, MyTaskType } from '../../api/work-item-contracts';
import { workItemsApi } from '../../api/work-items-api';
import { LoadError, useAdminData } from '../../components/admin-page';
import { useListReread } from '../../components/list-area';
import { searchFrom, type ICardLinkState } from '../../components/participant-link';
import { DD_DISCLOSURE_COLUMNS, ddDisclosuresOf, ddNextStep, ddStatusChip } from '../../dd';
import { catalogueSections } from '../../lists';
import { readMyListTaskKey, TEAM_PATHS } from '../../paths';
import { refusalMessage } from '../../refusal';
import { CellValue, cellText } from '../lists/cells';
import { DdBlock } from '../person-hub/due-diligence';
import { MetricsTask } from '../metrics-updates/task';
import { LEGACY_TAB } from '../reinstatement/page';
import { RecordLink, taskName, taskPath, typeWord } from './my-tasks-table';

/**
 * A Lists row assigned to the viewer, opened beside My tasks (SR-424) — the pane a work item has,
 * for the rows that are no work item (due-diligence design D6: the two models stay apart). **The
 * work is done here, with the product's own parts**, never a copy of them:
 *
 * - a DD call draws the record page's Due diligence block (`DdBlock`) — the interview and the
 *   outreach log, as DD calls' panel draws it — under the cadence's next step, for a viewer
 *   holding `internal_fields.read`, the block's reads' policy (DD calls' guard too);
 * - a reinstatement draws the row as the worklist reads it — the reason, its handling, the dates
 *   and the notes — and *Reinstate…* / *Keep out…*, the worklist's own decision for one row
 *   (`POST /admin/reinstatements`, at the Quality cell's version, the field found in the
 *   catalogue, never in the view's columns, which a colleague can edit); a row on legacy Travel 2
 *   or 3 (no handling) gets the worklist's own guidance instead, since the worklist decides none;
 * - any other row draws its view's fields, the work being whatever the view was for.
 *
 * **Every row can be taken off the viewer's tasks** — *Remove from my tasks*, the Lists
 * assignment cleared at its version (`sys.assigned_to`, `lists.assign` and
 * `internal_fields.write`): a list row has no state of its own that says it is done. A DD call
 * the viewer holds as its interviewer while the row is someone else's or nobody's (SR-525) is
 * theirs by the booking, not the assignment: it says whose the row is and offers no removal, which
 * would clear a colleague's assignment. A write
 * re-reads the task and the list beside it; a row the write took off the viewer's list (a
 * completed call, a decision, the removal) is said as such, never as an error, with the next task
 * of its type; one whose type moved (a reinstated advisor travels, a completed call is a plain
 * list row) says so. A decision survey-api did not apply (a conflict, a row off the worklist) is
 * said as a refusal beside the buttons, and re-reads.
 *
 * **What the row is, is survey-api's**: the task is My tasks' own read narrowed to this pair, and
 * its cells the Lists engine's, read through the view that holds it (`viewId`, its columns as My
 * tasks sent them — opening the view would stamp it as the viewer's last in Lists). The screen the
 * row comes from is one press away, at the very row: DD calls' panel (`?open=`), the reason's tab,
 * the view.
 */
export function AdminMyListTaskPage() {
  const { rowKey = '' } = useParams();
  return <MyListTask key={rowKey} rowKey={rowKey} />;
}

/** The leaf naming one participant — the row's own key in the list engine. */
const PARTICIPANT = 'sys.participant_id';
/** The Lists assignment, asked beside the view's columns: its version is what a removal sends. */
const ASSIGNED_TO = 'sys.assigned_to';
/** The row's name, the page's title. */
const NAME = 'sys.display_name';
/** A reinstatement row's handling: held by a reason the worklist reconsiders, never by legacy
 *  Travel 2 or 3, which has no reason. */
const REIN_HANDLING = 'rein.handling';

interface ITaskRead {
  task: Nullable<{ type: MyTaskType; row: IMyListTask }>;
  catalogue: Nullable<IListFieldCatalogue>;
  row: Nullable<IListRow>;
  /** The survey's Quality rating field — a reinstatement's version is its cell's. */
  quality: Nullable<string>;
}

/** The suffix survey-api names a survey's Quality rating field with. */
const QUALITY_RATING = '.quality_rating';

/** The survey's Quality rating field in its catalogue. */
function qualityField(catalogue: Nullable<IListFieldCatalogue>): Nullable<string> {
  for (const group of catalogue?.groups ?? []) {
    const field = group.fields.find((one) => one.code.endsWith(QUALITY_RATING));
    if (field) return field.code;
  }
  return null;
}

/** What a write on the page said, the type the task had then, and what *Undo* would send back. */
interface ISaid {
  text: string;
  type: MyTaskType;
  undo?: { participantId: string; survey: string; version: number };
}

/** Why *Remove from my tasks* is off, said once on the page (`NotPermitted`'s id). */
const REMOVE_REASON = 'my-task-remove-not-permitted';

function MyListTask({ rowKey }: { rowKey: string }) {
  const pair = readMyListTaskKey(rowKey);
  const can = useCan();
  const { user } = useAuth();
  const rereadList = useListReread();
  // What a write here said, and the type the task had then — kept over the re-read that may take
  // the task away or move it to another type (a reinstated advisor is a DD call next).
  const [said, setSaid] = React.useState<Nullable<ISaid>>(null);
  const { data, error, reload } = useAdminData<ITaskRead>(
    async (signal) => {
      if (pair === null) return { task: null, catalogue: null, row: null, quality: null };
      try {
        const [tasks, catalogue] = await Promise.all([
          workItemsApi.myTasks({ ...pair, pageSize: 1 }, signal),
          listsApi.fields(pair.survey, signal)
        ]);
        const found = tasks.items[0];
        const quality = qualityField(catalogue);
        if (!found?.row) return { task: null, catalogue, row: null, quality };
        const rows = await listsApi.rows(
          {
            query: {
              survey: pair.survey,
              viewId: found.row.viewId,
              columns: [
                ...new Set([
                  ...found.row.viewColumns,
                  ASSIGNED_TO,
                  ...(quality ? [quality] : []),
                  // A DD call's Disclosures line (SR-460), off the row as DD calls' panel draws it.
                  ...(found.type === 'dd_call' ? DD_DISCLOSURE_COLUMNS : [])
                ])
              ],
              criteria: {
                version: 1,
                op: 'and',
                children: [{ field: PARTICIPANT, op: 'eq', value: pair.participantId }]
              }
            },
            page: 1,
            pageSize: 25
          },
          signal
        );
        return {
          task: { type: found.type, row: found.row },
          catalogue,
          row: rows.rows[0] ?? null,
          quality
        };
      } catch (readError) {
        throw new Error(refusalMessage(readError, 'Could not read this task'));
      }
    },
    [rowKey]
  );
  const reread = () => {
    void reload();
    rereadList();
  };
  // A write with nothing to say re-reads and keeps what an earlier one said: a completed
  // interview says so and then tells the block's own change, which must not wipe it.
  const moved = (sentence: Nullable<string>, undoAt?: number) => {
    const type = data?.task?.type;
    if (sentence !== null && type !== undefined) {
      setSaid({
        text: sentence,
        type,
        ...(undoAt !== undefined && pair !== null ? { undo: { ...pair, version: undoAt } } : {})
      });
    }
    reread();
  };

  if (data === null || data.task === null) {
    return (
      <>
        <PageHeader title="Task" crumb="My tasks" />
        <div className="grid gap-6 px-6 py-6">
          {error ? (
            <LoadError message={error} />
          ) : data === null ? (
            <p className="text-sm text-muted-foreground">Reading the task…</p>
          ) : (
            <>
              <Alert>
                <AlertTitle>{said?.text ?? 'Not assigned to you'}</AlertTitle>
                <AlertDescription>
                  {said
                    ? 'It has left your tasks.'
                    : 'This row is no longer assigned to you — a colleague may have taken it, ' +
                      'or it was unassigned in Lists.'}
                </AlertDescription>
              </Alert>
              {said && (
                <div className="flex flex-wrap items-center gap-2">
                  {said.undo && (
                    <UndoRemoval
                      {...said.undo}
                      onUndone={() => {
                        setSaid({ text: 'Back on your tasks.', type: said.type });
                        reread();
                      }}
                    />
                  )}
                  <NextTask type={said.type} />
                </div>
              )}
            </>
          )}
        </div>
      </>
    );
  }

  const { type, row: task } = data.task;
  const cells = data.row?.cells ?? {};
  // A DD call the viewer holds as its interviewer, the row assigned to someone else or nobody.
  const held = type === 'dd_call' && data.row !== null && !!user && cells[ASSIGNED_TO]?.v !== user.accountId;

  return (
    <>
      <PageHeader
        title={task.displayName}
        crumb="My tasks"
        lede={[typeWord(type), task.surveyName, `cycle ${task.cycle}`, task.firmName]
          .filter(Boolean)
          .join(' · ')}
        actions={
          <>
            {type === 'dd_call' && !can('internal_fields.read') ? (
              // DD calls is guarded by `internal_fields.read`: drawn off, its reason said below.
              <Button size="sm" variant="outline" disabled data-task-source="">
                {sourceLabel(type)}
              </Button>
            ) : (
              <Button asChild size="sm" variant="outline">
                <Link to={sourcePath(type, task)} data-task-source="">
                  {sourceLabel(type)}
                </Link>
              </Button>
            )}
            {!held && <RemoveFromMyTasks task={task} row={data.row} onRemoved={moved} />}
          </>
        }
      />
      <div className="grid gap-6 px-6 py-6">
        {!held && !(can('internal_fields.write') && can('lists.assign')) && (
          <NotPermitted id={REMOVE_REASON} what="taking a row off your tasks (assigning rows)" />
        )}
        {held && (
          <p className="text-sm" data-dd-held="">
            You hold this DD call; the row is assigned to{' '}
            {cells[ASSIGNED_TO]?.label ?? (cells[ASSIGNED_TO]?.v ? 'a colleague' : 'nobody')}.
          </p>
        )}
        {said && (
          <div className="flex flex-wrap items-center gap-3">
            <p role="status" className="text-sm text-[var(--color-teal)]">
              {said.text}
              {said.type !== type && ` It is now a ${typeWord(type)} task.`}
            </p>
            {said.type !== type && <NextTask type={said.type} />}
          </div>
        )}
        {error && (
          <LoadError message={`Could not refresh — showing the task as last read. ${error}`} />
        )}
        <p className="text-sm">
          <RecordLink type={type} row={task} /> — the record page
          {type === 'dd_call' ? ', at its Due diligence block' : ''}.
        </p>
        {data.row === null && (
          <p className="text-sm text-muted-foreground">
            Lists holds no row for this person and survey any more — read My tasks again.
          </p>
        )}
        {type === 'dd_call' ? (
          <DdTask
            task={task}
            row={data.row}
            onChanged={() => moved(null)}
            onCompleted={() => moved(`DD call with ${task.displayName} completed.`)}
          />
        ) : (
          <>
            {type === 'reinstatement' && data.row && (
              <ReinstatementDecisions
                task={task}
                row={data.row}
                quality={data.quality}
                onDecided={moved}
                onRefused={() => moved(null)}
              />
            )}
            {type === 'metrics' && data.row && (
              <MetricsTask task={task} row={data.row} onDone={moved} />
            )}
            {type === 'list' && (
              <p className="text-sm text-muted-foreground">
                Assigned to you in {task.assignedIn?.name ?? 'Lists'} on{' '}
                {formatDateTime(task.assignedAt)} — on no DD or reinstatement list; its fields are edited on
                the record page or in Lists.
              </p>
            )}
            <RowFields task={task} catalogue={data.catalogue} cells={cells} />
          </>
        )}
      </div>
    </>
  );
}

/** The way on once a task has left: the first task of the same type still assigned, read
 *  afresh — never guessed from the list beside, which may be another type's or another page. */
function NextTask({ type }: { type: MyTaskType }) {
  // The list's own search, as the row that opened this task carried it.
  const search = searchFrom(useLocation().state);
  const next = useAdminData(
    (signal) => workItemsApi.myTasks({ type, pageSize: 1 }, signal),
    [type]
  );
  if (next.error) {
    return <p className="text-sm text-muted-foreground">Your next task could not be read.</p>;
  }
  const task = next.data?.items[0];
  if (!task) return null;
  return (
    <Button asChild size="sm" className="justify-self-start">
      <Link
        to={taskPath(task)}
        state={{ search, list: true } satisfies ICardLinkState}
        data-next-task=""
      >
        Next {typeWord(type)} task — {taskName(task)}
      </Link>
    </Button>
  );
}

/** A DD call: the cadence's next step over the record's own Due diligence block. */
function DdTask({
  task,
  row,
  onChanged,
  onCompleted
}: {
  task: IMyListTask;
  /** The Lists row, read with `DD_DISCLOSURE_COLUMNS`; null where Lists holds none. */
  row: Nullable<IListRow>;
  onChanged: () => void;
  onCompleted: () => void;
}) {
  const can = useCan();
  const readable = can('internal_fields.read');
  const schema = useAdminData(
    async () => (readable ? internalFieldsApi.schema(task.survey) : null),
    [task.survey, readable]
  );
  const status = task.ddStatus ?? 'none';
  const chip = ddStatusChip(status);
  return (
    <div className="grid gap-4">
      <p className="flex flex-wrap items-center gap-2 text-sm">
        <StatusChip tone={chip.tone}>{chip.label}</StatusChip>
        <span>
          Next step:{' '}
          <strong className="font-medium">
            {ddNextStep(status, task.facts.ddOutreach ?? 0, task.facts.ddScheduledAt)}
          </strong>
        </span>
        <span className="text-muted-foreground">
          {task.facts.ddLastOutreachAt
            ? `Last outreach ${formatDateTime(task.facts.ddLastOutreachAt)}`
            : 'No outreach yet'}
        </span>
      </p>
      {!readable ? (
        <NotPermitted what="reading due diligence" />
      ) : (
        <>
          {schema.error && <LoadError message={schema.error} />}
          <DdBlock
            participantId={task.participantId}
            surveyCode={task.survey}
            name={task.displayName}
            cycle={task.cycle}
            schema={schema.data}
            disclosures={row ? ddDisclosuresOf(row) : undefined}
            onChanged={onChanged}
            onCompleted={onCompleted}
            heading="h3"
          />
        </>
      )}
    </div>
  );
}

/** A reinstatement's two decisions, the worklist's own for one row — or, on legacy Travel 2 or 3,
 *  which the worklist decides by no button, its guidance. */
function ReinstatementDecisions({
  task,
  row,
  quality,
  onDecided,
  onRefused
}: {
  task: IMyListTask;
  row: IListRow;
  quality: Nullable<string>;
  onDecided: (sentence: string) => void;
  /** survey-api applied nothing: the row is read again, the refusal said here. */
  onRefused: () => void;
}) {
  const can = useCan();
  const writable = can('internal_fields.write');
  const [asking, setAsking] = React.useState<Nullable<ReinstatementDecision>>(null);
  const [refused, setRefused] = React.useState<Nullable<string>>(null);
  if (task.facts.handling === null) {
    return (
      <p className="text-sm">
        On legacy Travel 2 or 3 — reviewed against the year’s thresholds, and Travel set in Lists.
      </p>
    );
  }
  const reason = task.facts.reason?.label ?? task.facts.handling.label;
  const verb = asking === 'keep_out' ? 'Keep out' : 'Reinstate';
  return (
    <div className="grid gap-2">
      <p className="text-sm">
        Out of the ranking for <strong className="font-medium">{reason}</strong>
        {` — ${task.facts.handling.label}`}.
      </p>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" disabled={!writable} onClick={() => setAsking('reinstate')}>
          Reinstate…
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={!writable}
          onClick={() => setAsking('keep_out')}
        >
          Keep out…
        </Button>
      </div>
      {!writable && <NotPermitted what="deciding a reinstatement" />}
      {refused && (
        <p role="alert" className="text-sm text-[var(--color-deep-magenta)]">
          {refused}
        </p>
      )}
      <ReasonDialog
        open={asking !== null}
        onOpenChange={(open) => !open && setAsking(null)}
        title={`${verb} ${task.displayName}`}
        description={
          asking === 'keep_out'
            ? `“${reason}” stays, and the advisor leaves the worklist until the next cycle opens.`
            : `Travel becomes 1 and “${reason}” gives way to the advisor’s last score, ` +
              'or is cleared where there is none.'
        }
        reasonLabel="Note"
        required={false}
        max={2000}
        confirmLabel={verb}
        onConfirm={async (note) => {
          if (asking === null) return null;
          try {
            const result = await listsApi.reinstate({
              survey: task.survey,
              decision: asking,
              ...(note ? { note } : {}),
              participants: [
                {
                  participantId: task.participantId,
                  version: quality === null ? 0 : (row.cells[quality]?.version ?? 0)
                }
              ]
            });
            setAsking(null);
            if (result.applied.length > 0) {
              setRefused(null);
              const done = asking === 'reinstate' ? 'reinstated' : 'kept out';
              onDecided(`${task.displayName}: ${done}.`);
            } else {
              setRefused(
                result.conflicts.length > 0
                  ? `A colleague changed ${task.displayName}’s Quality rating since you read ` +
                      'it — nothing was decided. The row has been read again.'
                  : `${task.displayName} is no longer on the worklist — nothing was decided.`
              );
              onRefused();
            }
            return null;
          } catch (decideError) {
            return refusalMessage(decideError, 'Could not save the decision');
          }
        }}
      />
    </div>
  );
}

/** *Remove from my tasks*: the Lists assignment cleared at the version it was read with. */
function RemoveFromMyTasks({
  task,
  row,
  onRemoved
}: {
  task: IMyListTask;
  row: Nullable<IListRow>;
  /** Told with the version the clear left the cell at — what *Undo* sends back. */
  onRemoved: (sentence: string, version: number) => void;
}) {
  const can = useCan();
  const writable = can('internal_fields.write') && can('lists.assign');
  const [busy, setBusy] = React.useState(false);
  const [failed, setFailed] = React.useState<Nullable<string>>(null);
  return (
    <span className="inline-flex items-center gap-2">
      <Button
        size="sm"
        variant="ghost"
        disabled={!writable || busy || row === null}
        aria-describedby={writable ? undefined : REMOVE_REASON}
        onClick={async () => {
          if (row === null) return;
          setBusy(true);
          setFailed(null);
          try {
            const answer = await writeAssignment(
              { participantId: task.participantId, survey: task.survey },
              null,
              row.cells[ASSIGNED_TO]?.version ?? 0,
              'Removed from My tasks'
            );
            const written = [...answer.applied, ...answer.unchanged][0];
            if (answer.conflicts.length > 0 || written === undefined) {
              setFailed('A colleague changed this assignment since you read it.');
              setBusy(false);
            } else {
              // Held until the re-read takes the task away: a second press would send the old
              // version and be told of a colleague who is the viewer.
              onRemoved(`${task.displayName} removed from your tasks`, written.version);
            }
          } catch (removeError) {
            setFailed(refusalMessage(removeError, 'Could not remove the task'));
            setBusy(false);
          }
        }}
      >
        Remove from my tasks
      </Button>
      {failed && <Refused text={failed} />}
    </span>
  );
}

/** *Undo* after a removal: the assignment given back to the viewer at the version the clear left —
 *  a colleague who took the row since meets a conflict, said, and keeps it. */
function UndoRemoval({
  participantId,
  survey,
  version,
  onUndone
}: {
  participantId: string;
  survey: string;
  version: number;
  onUndone: () => void;
}) {
  const { user } = useAuth();
  const [busy, setBusy] = React.useState(false);
  const [failed, setFailed] = React.useState<Nullable<string>>(null);
  return (
    <>
      <Button
        size="sm"
        variant="outline"
        disabled={busy || !user}
        data-undo-removal=""
        onClick={async () => {
          if (!user) return;
          setBusy(true);
          setFailed(null);
          try {
            const answer = await writeAssignment(
              { participantId, survey },
              user.accountId,
              version,
              'Put back on My tasks'
            );
            if (answer.conflicts.length > 0) {
              const taken = answer.conflicts[0].current;
              setFailed(
                taken?.v
                  ? 'A colleague has taken this row since — it stays theirs.'
                  : 'The assignment changed since — read it again in Lists.'
              );
            } else {
              onUndone();
            }
          } catch (undoError) {
            setFailed(refusalMessage(undoError, 'Could not put the task back'));
          } finally {
            setBusy(false);
          }
        }}
      >
        Undo
      </Button>
      {failed && <Refused text={failed} />}
    </>
  );
}

/** The row's Lists assignment written at a version — *Remove from my tasks* and *Undo* alike. */
function writeAssignment(
  pair: { participantId: string; survey: string },
  value: Nullable<string>,
  version: number,
  note: string
) {
  return listsApi.edit({ note, cells: [{ ...pair, field: ASSIGNED_TO, value, version }] });
}

/** What a removal or its *Undo* was refused, beside its button. */
function Refused({ text }: { text: string }) {
  return (
    <span role="alert" className="text-xs text-[var(--color-deep-magenta)]">
      {text}
    </span>
  );
}

/** The row's fields, as Lists' reading pane draws them: the view's columns, by catalogue group. */
function RowFields({
  task,
  catalogue,
  cells
}: {
  task: IMyListTask;
  catalogue: Nullable<IListFieldCatalogue>;
  cells: IListRow['cells'];
}) {
  // What the page already says above is not said again: the name, the assignee, the reason.
  const said = new Set([NAME, ASSIGNED_TO, REIN_HANDLING]);
  const shown = new Set(
    task.viewColumns.filter((code) => !said.has(code) && !code.endsWith(QUALITY_RATING))
  );
  return (
    <>
      {catalogueSections(catalogue, (field) => shown.has(field.code), shown).map((section) => (
        <div key={section.key} className="grid gap-2">
          <h2 className="text-xs font-medium tracking-wide text-[var(--veritant-royal-70)] uppercase">
            {section.title}
          </h2>
          <FieldGrid>
            {section.fields.map((field) => (
              <FieldGridItem
                key={field.code}
                label={field.number ? `${field.number} ${field.label}` : field.label}
                filled={cellText(field, cells[field.code]) !== ''}
              >
                <CellValue field={field} cell={cells[field.code]} look={null} whole />
              </FieldGridItem>
            ))}
          </FieldGrid>
        </div>
      ))}
    </>
  );
}

function sourceLabel(type: MyTaskType): string {
  if (type === 'dd_call') return 'Open on DD calls';
  if (type === 'reinstatement') return 'Open in Reinstatement';
  if (type === 'metrics') return 'Open in Metrics updates';
  return 'Open in Lists';
}

/** The screen the row comes from, at this row: the DD panel, the reason's tab, the view. */
function sourcePath(type: MyTaskType, task: IMyListTask): string {
  const params = new URLSearchParams({ survey: task.survey });
  if (type === 'dd_call') {
    params.set('q', task.displayName);
    params.set('open', task.participantId);
    return `${TEAM_PATHS.ddCalls}?${params.toString()}`;
  }
  if (type === 'reinstatement') {
    const reason = task.facts.handling ? task.facts.reason : null;
    params.set('tab', reason ? reason.code : LEGACY_TAB);
    params.set('q', task.displayName);
    return `${TEAM_PATHS.reinstatement}?${params.toString()}`;
  }
  if (type === 'metrics') {
    // The task is any owed advisor, DD call or not: the page opens with the DD condition off.
    params.set('q', task.displayName);
    params.set('whose', 'me');
    params.set('dd', 'off');
    return `${TEAM_PATHS.metricsUpdates}?${params.toString()}`;
  }
  // The view it was assigned from, else the survey's My tasks view.
  const view = task.assignedIn?.viewId ?? task.viewId;
  if (view) params.set('view', view);
  return `${TEAM_PATHS.lists}?${params.toString()}`;
}
